import {DatePicker} from '@material-ui/pickers';
import {FilterEnum} from 'Agent/Accounts/ducks/account';
import {DateRange} from 'Core/models/DateRange';
import {FilterOptionUnion} from 'Core/models/FilterOption';
import {InequalityRangeFilter} from 'Core/models/InequalityRange';
import React from 'react';
import DateFilter from 'Shared/components/DateFilter';
import DropDownFilter, {
  DropDownFilterProps,
  MultiSelectProps,
  SingleSelectProps,
} from 'Shared/components/DropDownFilter';
import InequalityFilter from 'Shared/components/InequalityFilter';
import TextFilter from 'Shared/components/TextFilter';
import {GetPropertiesOfType} from 'Shared/utilities/GetPropertiesOfType';
import {fold as foldOption, fromNullable} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';

export const withOptionField = <TFilter extends {}, TKey extends keyof TFilter>(
  fieldName: string,
  fieldPropertyName: TKey,
  changeFilter: (fieldPropertyName: TKey) => (value: any) => void,
  options: FilterEnum[],
  multiple: boolean
) => (filter: TFilter) => {
  let uniqueProps;

  if (multiple) {
    uniqueProps = {
      value: (filter[fieldPropertyName] as unknown) as FilterOptionUnion[],
      multiple: true,
    } as MultiSelectProps;
  } else {
    uniqueProps = {
      value: (filter[fieldPropertyName] as unknown) as FilterOptionUnion,
      multiple: false,
    } as SingleSelectProps;
  }

  const filterProps: DropDownFilterProps = {
    label: fieldName,
    items: options,
    onChange: changeFilter(fieldPropertyName),
    ...uniqueProps,
  };

  return <DropDownFilter {...filterProps} />;
};

export const withDateRangeField = <TFilter extends {}, TKey extends keyof TFilter>(
  fieldName: string,
  fieldPropertyName: TKey,
  changeFilter: (fieldPropertyName: TKey) => (value: any) => void
) => (filter: TFilter) => {
  const dateRange = (filter[fieldPropertyName] as unknown) as DateRange;

  return (
    <DateFilter
      startValue={dateRange.startDate}
      endValue={dateRange.endDate}
      onChangeEndDate={date => changeFilter(fieldPropertyName)({...dateRange, endDate: date ? new Date(date) : ''})}
      onChangeStartDate={date => changeFilter(fieldPropertyName)({...dateRange, startDate: date ? new Date(date) : ''})}
    />
  );
};

export const withInequalityField = <TFilter extends {}, TKey extends keyof TFilter>(
  fieldName: string,
  fieldPropertyName: TKey,
  changeFilter: (fieldPropertyName: TKey) => (value: any) => void
) => (filter: TFilter) => {
  const inequalityRange = (filter[fieldPropertyName] as unknown) as InequalityRangeFilter;

  return <InequalityFilter inequalityRange={inequalityRange} onChange={changeFilter(fieldPropertyName)} />;
};

const formatRegExp = (input: string, pattern: RegExp) =>
  pipe(
    fromNullable(input.match(pattern)),
    foldOption(
      () => '',
      input => input.join('')
    )
  );

export const withTextField = <TFilter extends {}, TKey extends keyof GetPropertiesOfType<TFilter, string | number>>(
  fieldName: string,
  fieldPropertyName: TKey,
  changeFilter: (fieldPropertyName: TKey) => (value: string) => void
) => (filter: TFilter) => (
  <TextFilter
    key={fieldName}
    placeholder={fieldName}
    onChange={changeFilter(fieldPropertyName)}
    value={(filter[fieldPropertyName] as unknown) as string}
  />
);

export const withRegExTextField = <TFilter extends {}, TKey extends keyof GetPropertiesOfType<TFilter, string>>(
  fieldName: string,
  fieldPropertyName: TKey,
  changeFilter: (fieldPropertyName: TKey) => (value: string) => void,
  pattern?: RegExp
) => (filter: TFilter) => (
  <TextFilter
    key={fieldName}
    placeholder={fieldName}
    onChange={filterInput =>
      changeFilter(fieldPropertyName)(pattern ? formatRegExp(filterInput, pattern) : filterInput)
    }
    value={(filter[fieldPropertyName] as unknown) as string}
  />
);

export const withDateField = <TFilter extends {}, TKey extends keyof TFilter>(
  fieldName: string,
  fieldPropertyName: TKey,
  changeFilter: (fieldPropertyName: TKey) => (value: any) => void
) => (filter: TFilter) => {
  return (
    <div className="date-filter">
      <DatePicker
        clearable
        color="primary"
        label={fieldName}
        format="MMMM d, yyyy"
        value={((filter[fieldPropertyName] as unknown) as Date) || null}
        onChange={date => changeFilter(fieldPropertyName)(date?.toISO())}
      />
    </div>
  );
};
